import { X } from "lucide-react";
import { classLabel, painLabel, platformLabel } from "@/lib/commentLabels";
import { Button } from "@/components/ui/button";

export type ActiveFilterChip = {
  key: string;
  label: string;
  onRemove: () => void;
};

type Props = {
  chips: ActiveFilterChip[];
  onClearAll: () => void;
};

export default function CommentsActiveFilters({ chips, onClearAll }: Props) {
  if (chips.length === 0) return null;

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <span className="text-xs font-medium text-slate-500">Filtry:</span>
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
        >
          {chip.label}
          <X className="h-3 w-3 text-slate-400" />
        </button>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-slate-500"
        onClick={onClearAll}
      >
        Wyczyść wszystko
      </Button>
    </div>
  );
}

export function buildCommentFilterChips(
  filters: {
    q: string;
    platform?: string;
    storyClass?: string;
    pain?: string;
    month?: string;
    postId?: string;
    window?: string;
    action?: boolean;
    cta?: boolean;
    hideReplies?: boolean;
  },
  handlers: {
    clearQ: () => void;
    clearPlatform: () => void;
    clearClass: () => void;
    clearPain: () => void;
    clearMonth: () => void;
    clearPost: () => void;
    clearWindow: () => void;
    clearAction: () => void;
    clearCta: () => void;
    showReplies: () => void;
  },
): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];
  if (filters.q.trim()) {
    chips.push({
      key: "q",
      label: `Szukaj: „${filters.q.trim().slice(0, 40)}”`,
      onRemove: handlers.clearQ,
    });
  }
  if (filters.platform) {
    chips.push({
      key: "platform",
      label: platformLabel(filters.platform),
      onRemove: handlers.clearPlatform,
    });
  }
  if (filters.storyClass) {
    chips.push({
      key: "class",
      label: classLabel(filters.storyClass),
      onRemove: handlers.clearClass,
    });
  }
  if (filters.pain) {
    chips.push({
      key: "pain",
      label: painLabel(filters.pain),
      onRemove: handlers.clearPain,
    });
  }
  if (filters.month) {
    chips.push({
      key: "month",
      label: filters.month,
      onRemove: handlers.clearMonth,
    });
  }
  if (filters.postId) {
    chips.push({
      key: "post",
      label: `Rolka: ${filters.postId.slice(0, 24)}`,
      onRemove: handlers.clearPost,
    });
  }
  if (filters.window && filters.window !== "30d") {
    const windowLabel =
      filters.window === "all" ? "Całość" : filters.window === "24h" ? "24 godziny" : filters.window;
    chips.push({
      key: "window",
      label: windowLabel,
      onRemove: handlers.clearWindow,
    });
  }
  if (filters.action) {
    chips.push({ key: "action", label: "Do akcji", onRemove: handlers.clearAction });
  }
  if (filters.cta) {
    chips.push({ key: "cta", label: "CTA Mama", onRemove: handlers.clearCta });
  }
  if (filters.hideReplies === false) {
    chips.push({ key: "replies", label: "Z replies", onRemove: handlers.showReplies });
  }
  return chips;
}
